import { z } from 'zod';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { discoveryKeys } from './discovery';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Engagement -> Discovery -> Sessions (T-0186): interviews (T-0173),
 * people (T-0172), questionnaires, the department round and secure links
 * (T-0175). Everything is under `/api/v1/engagements/:id/discovery`.
 *
 * Contracts: `apps/api/src/services/discovery-interviews.ts`,
 * `discovery-people.ts`, `discovery-questionnaires.ts`, `secure-links.ts`.
 * Every state, count and coverage change is computed on the server
 * (`@caliber-2/discovery-core`); the screen words it. Writes carry an
 * `Idempotency-Key`, one per action. A write invalidates the whole
 * discovery cache of the engagement, so the tab counts, the list chips and
 * the detail all move together.
 */

const base = (engagementId: string) =>
  `/api/v1/engagements/${encodeURIComponent(engagementId)}/discovery`;
const seg = encodeURIComponent;

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

export const InterviewStateSchema = z.object({
  kind: z.enum([
    'cancelled',
    'reading',
    'match_speakers',
    'review',
    'reading_failed',
    'held',
    'notes_entered',
    'reviewed',
    'scheduled',
    'not_scheduled',
    'optional',
  ]),
  progress: z.number().nullable().optional(),
  pending: z.number().optional(),
  /** `match_speakers`: proposals held on speakers not matched, or only suggested by the AI. */
  held: z.number().optional(),
  /** A reviewed transcript whose reading found no evidence in any version. */
  nothing_found: z.boolean().optional(),
});
export type InterviewState = z.infer<typeof InterviewStateSchema>;

const InterviewStatusSchema = z.enum(['planned', 'scheduled', 'held', 'cancelled']);
export type InterviewStatus = z.infer<typeof InterviewStatusSchema>;

const CoverageStatusSchema = z.enum(['corroborated', 'partial', 'none']);

/** What a write changed in coverage (computed by `computeCoverage`). */
export const CoverageChangeSchema = z.object({
  area_id: z.string(),
  code: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  status_before: CoverageStatusSchema.nullable(),
  status_after: CoverageStatusSchema.nullable(),
  sources_before: z.number(),
  sources_after: z.number(),
  confirmed_before: z.number().optional(),
  confirmed_after: z.number().optional(),
  same_person_only: z.boolean(),
});
export type CoverageChange = z.infer<typeof CoverageChangeSchema>;

export const RunLinkSchema = z.object({
  id: z.string(),
  status: z.string(),
  email: z.string().nullable().optional(),
  issued_at: z.string(),
  expires_at: z.string(),
  ceiling_at: z.string().optional(),
  extended_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
});
export type RunLink = z.infer<typeof RunLinkSchema>;

// ---------------------------------------------------------------------------
// Interviews
// ---------------------------------------------------------------------------

const InterviewListItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  status: InterviewStatusSchema,
  optional: z.boolean(),
  people: z.array(
    z.object({ id: z.string(), full_name: z.string(), title: z.string().nullable().optional() }),
  ),
  scheduled_at: z.string().nullable(),
  held_at: z.string().nullable().optional(),
  state: InterviewStateSchema,
  needs_you: z.boolean(),
});
export type InterviewListItem = z.infer<typeof InterviewListItemSchema>;

const InterviewListSchema = z.object({
  groups: z.array(z.object({ group: z.string(), items: z.array(InterviewListItemSchema) })),
  department_round: z
    .object({
      round_id: z.string(),
      questionnaire: z.object({ id: z.string(), code: z.string(), name: z.string() }),
      answered: z.number(),
      departments: z.number(),
    })
    .nullable(),
});
export type InterviewList = z.infer<typeof InterviewListSchema>;

const InterviewDetailSchema = z.object({
  interview: z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
    group_label: z.string(),
    interview_type: z.object({ id: z.string(), code: z.string(), name: z.string() }).nullable(),
    status: InterviewStatusSchema,
    optional: z.boolean(),
    scheduled_at: z.string().nullable(),
    duration_minutes: z.number(),
    held_at: z.string().nullable(),
    cancelled: z
      .object({
        reason: z.string(),
        by_name: z.string().nullable(),
        at: z.string(),
      })
      .nullable(),
    invite: z.object({ sent_at: z.string(), recipients: z.array(z.string()) }).nullable(),
    created_at: z.string(),
    state: InterviewStateSchema,
  }),
  people: z.array(
    z.object({
      id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      department: z.string().nullable(),
      email: z.string().nullable(),
    }),
  ),
  areas: z.array(z.object({ id: z.string(), code: z.string(), label: z.string() })),
  questions: z.array(
    z.object({
      id: z.string(),
      kind: z.string(),
      text: z.string(),
      area_id: z.string().nullable(),
      ai_reason: z.string().nullable(),
      included: z.boolean(),
    }),
  ),
  demos: z.array(z.object({ id: z.string(), text: z.string(), included: z.boolean() })),
  questionnaire: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      channel: z.enum(['live', 'link']),
      answered: z.number(),
      total: z.number(),
    })
    .nullable(),
  transcript: z.object({ state: z.string(), progress: z.number().nullable() }),
});
export type InterviewDetail = z.infer<typeof InterviewDetailSchema>;

const InterviewPlanSchema = z.object({
  suggested_type_id: z.string().nullable(),
  matched_keywords: z.array(z.string()),
  type: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      duration_minutes: z.number(),
      questionnaire_id: z.string().nullable(),
    })
    .nullable(),
  area_ids: z.array(z.string()),
  questions: z.array(
    z.object({
      key: z.string(),
      kind: z.enum(['type', 'area', 'ai']),
      question_id: z.string().nullable(),
      area_id: z.string().nullable(),
      text: z.string(),
      ai_proposal_id: z.string().nullable(),
      ai_reason: z.string().nullable(),
      default_included: z.boolean(),
    }),
  ),
  demos: z.array(
    z.object({
      key: z.string(),
      question_id: z.string(),
      text: z.string(),
      default_included: z.boolean(),
    }),
  ),
  questionnaire: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      questions: z.number(),
      minutes: z.number(),
    })
    .nullable(),
  duration_minutes: z.number(),
  dropped_area_tags: z.array(
    z.object({ area_id: z.string(), code: z.string(), label: z.string() }),
  ),
});
export type InterviewPlan = z.infer<typeof InterviewPlanSchema>;

export interface PlanParams {
  title: string;
  department: string;
  /** A type id, `other`, or absent to take the suggestion. */
  interview_type_id?: string;
  /** Explicit areas; absent means the type's. */
  area_ids?: string[];
}

export type PersonMatchDecision =
  | { decision: 'different'; candidate_ids: string[] }
  | { decision: 'same'; person_id: string };

export interface CreateInterviewBody {
  people: Array<
    | { person_id: string }
    | {
        full_name: string;
        title?: string;
        department?: string;
        email?: string;
        match_decision?: PersonMatchDecision;
      }
  >;
  interview_type_id: string | null;
  area_ids: string[];
  questions: Array<{
    kind: 'type' | 'area' | 'ai';
    question_id?: string;
    ai_proposal_id?: string;
    included: boolean;
  }>;
  demos: Array<{ question_id: string; included: boolean }>;
  questionnaire_channel?: 'live' | 'link';
  scheduled_at?: string;
  duration_minutes: number;
  send_invite?: boolean;
}

const CreateInterviewResultSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: InterviewStatusSchema,
  scheduled_at: z.string().nullable(),
  invites_sent: z.number(),
  questionnaire_links_sent: z.number(),
});
export type CreateInterviewResult = z.infer<typeof CreateInterviewResultSchema>;

const ScheduleResultSchema = z.object({
  status: z.literal('scheduled'),
  scheduled_at: z.string(),
  invites_sent: z.number(),
  invites_failed: z.array(z.object({ email: z.string() }).passthrough()).optional(),
});
export type ScheduleResult = z.infer<typeof ScheduleResultSchema>;

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const PersonRefSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
});
export type PersonRef = z.infer<typeof PersonRefSchema>;

const PersonSchema = PersonRefSchema.extend({
  email: z.string().nullable(),
  source: z.string(),
  status: z.string(),
  possible_duplicate_of: PersonRefSchema.nullable(),
  merged_into: PersonRefSchema.nullable(),
});
export type Person = z.infer<typeof PersonSchema>;

const PersonListItemSchema = PersonSchema.extend({
  interviews: z.array(z.string()),
  evidence_confirmed: z.number(),
});
export type PersonListItem = z.infer<typeof PersonListItemSchema>;

const PeopleListSchema = z.object({ items: z.array(PersonListItemSchema), total: z.number() });

export const CandidateSchema = z.object({
  person_id: z.string(),
  full_name: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
  email: z.string().nullable(),
  match: z.string(),
  possible_duplicate: z.boolean(),
  interviews: z.array(z.object({ id: z.string(), code: z.string(), title: z.string() })),
});
export type Candidate = z.infer<typeof CandidateSchema>;

const PersonDetailSchema = z.object({
  person: PersonSchema,
  interviews: z.array(
    z.object({ id: z.string(), code: z.string(), title: z.string(), state: z.string() }),
  ),
  answers: z.array(
    z.object({
      id: z.string(),
      questionnaire: z.object({ id: z.string(), code: z.string(), name: z.string().nullable() }),
      prompt_as_asked: z.string(),
      answered_at: z.string(),
      evidence_number: z.string().nullable(),
      superseded: z.boolean(),
    }),
  ),
  evidence: z.array(
    z.object({
      id: z.string(),
      number: z.string().nullable(),
      status: z.string(),
      text: z.string(),
      area_ids: z.array(z.string()),
      channel: z.string(),
      citation: z.object({
        kind: z.string(),
        interview_code: z.string().nullable(),
        at_ms: z.number().nullable(),
        paragraph_no: z.number().nullable(),
        question_code: z.string().nullable(),
      }),
    }),
  ),
  merges: z.array(
    z.object({
      id: z.string(),
      merged_person: PersonRefSchema,
      kept_person: PersonRefSchema,
      merged_at: z.string(),
      merged_by_name: z.string().nullable(),
      undone_at: z.string().nullable(),
      undoable: z.boolean(),
    }),
  ),
  possible_duplicate_of: PersonRefSchema.extend({ match: z.string().nullable() }).nullable(),
  retired: z
    .object({
      by_name: z.string().nullable(),
      at: z.string().nullable(),
      reason: z.string().nullable(),
    })
    .nullable(),
  attached: z.object({
    interviews: z.number(),
    answers: z.number(),
    evidence: z.number(),
    speaker_mappings: z.number(),
    follow_ups: z.number(),
    department_rounds: z.number(),
    open_links: z.number(),
  }),
  can_retire: z.boolean(),
});
export type PersonDetail = z.infer<typeof PersonDetailSchema>;

const PersonWriteSchema = z.object({ person: PersonSchema }).passthrough();
const MergeResultSchema = z
  .object({ merge_id: z.string(), coverage_changes: z.array(CoverageChangeSchema) })
  .passthrough();
const ResolveResultSchema = z
  .object({
    decision: z.enum(['merge', 'different']),
    merge_id: z.string().nullable(),
    coverage_changes: z.array(CoverageChangeSchema),
  })
  .passthrough();

export interface PersonPatch {
  full_name?: string;
  title?: string | null;
  department?: string | null;
  email?: string | null;
}

// ---------------------------------------------------------------------------
// Questionnaires, rounds, links
// ---------------------------------------------------------------------------

export const ANSWER_TYPES = ['text', 'number', 'rating', 'single', 'multiple'] as const;
export type AnswerType = (typeof ANSWER_TYPES)[number];
export type AnswerValue =
  | { text: string }
  | { rating: number }
  | { option: string }
  | { options: string[] };

const RunQuestionSchema = z.object({
  run_question_id: z.string(),
  number: z.number(),
  prompt: z.string(),
  answer_type: z.enum(ANSWER_TYPES),
  options: z.array(z.string()),
  area_ids: z.array(z.string()),
  answer: z
    .object({
      answer_id: z.string(),
      text: z.string(),
      channel: z.string(),
      answered_at: z.string(),
      evidence_number: z.string().nullable(),
      held: z.boolean(),
    })
    .nullable(),
});
export type RunQuestion = z.infer<typeof RunQuestionSchema>;

const InterviewQuestionnaireSchema = z.object({
  run_id: z.string(),
  status: z.string(),
  person: z.object({
    id: z.string(),
    full_name: z.string(),
    email: z.string().nullable(),
    possible_duplicate: z.boolean(),
  }),
  questionnaire: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    minutes: z.number(),
  }),
  questions: z.array(RunQuestionSchema),
  answered: z.number(),
  total: z.number(),
  link: RunLinkSchema.nullable(),
});
export type InterviewQuestionnaire = z.infer<typeof InterviewQuestionnaireSchema>;

const LiveAnswerResultSchema = z.object({
  answer_id: z.string(),
  evidence: z.object({
    id: z.string(),
    number: z.string().nullable(),
    held: z.boolean(),
    area_ids: z.array(z.string()),
  }),
  coverage_changes: z.array(CoverageChangeSchema),
});
export type LiveAnswerResult = z.infer<typeof LiveAnswerResultSchema>;

const SendQuestionnaireResultSchema = z.object({
  link_id: z.string(),
  expires_at: z.string(),
  sent_to: z.string(),
  question_count: z.number(),
});

const RoundDepartmentSchema = z.object({
  id: z.string(),
  department: z.string(),
  contact: z
    .object({
      id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      email: z.string().nullable(),
      possible_duplicate: z.boolean(),
      retired: z.boolean(),
      merged: z.boolean(),
    })
    .nullable(),
  status: z.enum(['not_sent', 'sent', 'answered']),
  answered_by: z.object({ id: z.string(), full_name: z.string() }).nullable(),
  answered_at: z.string().nullable(),
  link: RunLinkSchema.nullable(),
});
export type RoundDepartment = z.infer<typeof RoundDepartmentSchema>;

const RoundSchema = z.object({
  id: z.string(),
  questionnaire: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    minutes: z.number(),
    questions: z.number(),
  }),
  sent_at: z.string().nullable(),
  departments: z.array(RoundDepartmentSchema),
  answered: z.number(),
  total: z.number(),
});
export type Round = z.infer<typeof RoundSchema>;

const RoundSendResultSchema = z.object({
  sent: z.array(z.string()),
  skipped: z.array(z.object({ department_id: z.string(), reason: z.string() })),
});
const RoundRemindResultSchema = z.object({
  reminded: z.array(z.string()),
  skipped: z.array(z.object({ department_id: z.string(), reason: z.string() })),
});

const QuestionnairePreviewSchema = z.object({
  payload: z.object({
    title: z.string(),
    audience_line: z.string().nullable(),
    minutes: z.number().nullable(),
    questions: z.array(
      z.object({
        ordinal: z.number(),
        prompt: z.string(),
        answer_type: z.enum(ANSWER_TYPES),
        options: z.array(z.string()),
      }),
    ),
  }),
});
export type QuestionnairePreview = z.infer<typeof QuestionnairePreviewSchema>;

const LinkSchema = z.object({ id: z.string(), expires_at: z.string() }).passthrough();

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

const post = <T>(path: string, schema: z.ZodType<T>, body: unknown = {}) =>
  apiClient(path, schema, { method: 'POST', body, idempotencyKey: newIdempotencyKey() });

export const listInterviews = (e: string) =>
  apiClient(`${base(e)}/interviews`, InterviewListSchema, { query: { filter: 'all' } });
export const getInterview = (e: string, id: string) =>
  apiClient(`${base(e)}/interviews/${seg(id)}`, InterviewDetailSchema);
export const getInterviewPlan = (e: string, p: PlanParams) =>
  apiClient(`${base(e)}/interview-plan`, InterviewPlanSchema, {
    query: {
      title: p.title || undefined,
      department: p.department || undefined,
      interview_type_id: p.interview_type_id,
      area_ids: p.area_ids?.join(','),
    },
  });
export const createInterview = (e: string, body: CreateInterviewBody) =>
  post(`${base(e)}/interviews`, CreateInterviewResultSchema, body);
export const scheduleInterview = (
  e: string,
  id: string,
  body: { scheduled_at: string; send_invite: boolean },
) => post(`${base(e)}/interviews/${seg(id)}:schedule`, ScheduleResultSchema, body);
export const markInterviewHeld = (e: string, id: string) =>
  post(`${base(e)}/interviews/${seg(id)}:mark-held`, z.object({ held_at: z.string() }));
export const cancelInterview = (e: string, id: string, reason: string) =>
  post(`${base(e)}/interviews/${seg(id)}:cancel`, z.object({ status: z.string() }), { reason });
/** Name who a plan row will interview: an existing person (the check ran when they were added). */
export const addInterviewPerson = (e: string, id: string, personId: string) =>
  post(`${base(e)}/interviews/${seg(id)}/people`, z.object({ added: z.boolean() }).passthrough(), {
    person_id: personId,
  });
/** A plan row nobody will hold: optional leaves the plan count and keeps its code. */
export const setInterviewOptional = (e: string, id: string, optional: boolean) =>
  apiClient(`${base(e)}/interviews/${seg(id)}`, z.object({}).passthrough(), {
    method: 'PATCH',
    body: { optional },
    idempotencyKey: newIdempotencyKey(),
  });

export const listPeople = (e: string) => apiClient(`${base(e)}/people`, PeopleListSchema);
export const getPerson = (e: string, id: string) =>
  apiClient(`${base(e)}/people/${seg(id)}`, PersonDetailSchema);
export const checkPerson = (e: string, body: { full_name: string; email?: string }) =>
  apiClient(`${base(e)}/people:check`, z.object({ candidates: z.array(CandidateSchema) }), {
    method: 'POST',
    body,
  });
export const updatePerson = (e: string, id: string, body: PersonPatch) =>
  apiClient(`${base(e)}/people/${seg(id)}`, PersonWriteSchema, {
    method: 'PATCH',
    body,
    idempotencyKey: newIdempotencyKey(),
  });
export const resolveDuplicate = (e: string, id: string, decision: 'merge' | 'different') =>
  post(`${base(e)}/people/${seg(id)}:resolve-duplicate`, ResolveResultSchema, { decision });
export const mergePerson = (e: string, id: string, intoPersonId: string) =>
  post(`${base(e)}/people/${seg(id)}:merge`, MergeResultSchema, { into_person_id: intoPersonId });
export const undoMerge = (e: string, mergeId: string) =>
  post(
    `${base(e)}/people/merges/${seg(mergeId)}:undo`,
    z.object({ coverage_changes: z.array(CoverageChangeSchema) }).passthrough(),
  );
export const retirePerson = (e: string, id: string, action: 'retire' | 'restore') =>
  post(`${base(e)}/people/${seg(id)}:${action}`, PersonWriteSchema);

export const getInterviewQuestionnaire = (e: string, id: string) =>
  apiClient(`${base(e)}/interviews/${seg(id)}/questionnaire`, InterviewQuestionnaireSchema);
export const recordLiveAnswer = (
  e: string,
  id: string,
  body: { run_question_id: string; value: AnswerValue },
) => post(`${base(e)}/interviews/${seg(id)}/questionnaire/answers`, LiveAnswerResultSchema, body);
export const sendInterviewQuestionnaire = (e: string, id: string) =>
  post(`${base(e)}/interviews/${seg(id)}/questionnaire:send`, SendQuestionnaireResultSchema, {
    only_unanswered: true,
  });
export const extendLink = (e: string, linkId: string) =>
  post(`${base(e)}/links/${seg(linkId)}:extend`, LinkSchema);
export const getQuestionnairePreview = (e: string, questionnaireId: string, department?: string) =>
  apiClient(
    `${base(e)}/questionnaires/${seg(questionnaireId)}/preview`,
    QuestionnairePreviewSchema,
    { query: { department } },
  );

export const getRound = (e: string, id: string) =>
  apiClient(`${base(e)}/rounds/${seg(id)}`, RoundSchema);
export const sendRound = (e: string, id: string, departmentIds?: string[]) =>
  post(
    `${base(e)}/rounds/${seg(id)}:send`,
    RoundSendResultSchema,
    departmentIds ? { department_ids: departmentIds } : {},
  );
export const remindRound = (e: string, id: string, departmentIds: string[]) =>
  post(`${base(e)}/rounds/${seg(id)}:remind`, RoundRemindResultSchema, {
    department_ids: departmentIds,
  });
export const setRoundContact = (
  e: string,
  roundId: string,
  departmentId: string,
  personId: string,
) =>
  apiClient(
    `${base(e)}/rounds/${seg(roundId)}/departments/${seg(departmentId)}`,
    z.object({}).passthrough(),
    {
      method: 'PATCH',
      body: { contact: { person_id: personId } },
      idempotencyKey: newIdempotencyKey(),
    },
  );

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const sessionKeys = {
  interviews: (e: string) => [...discoveryKeys.all(e), 'interviews'] as const,
  interview: (e: string, id: string) => [...discoveryKeys.all(e), 'interview', id] as const,
  plan: (e: string, p: PlanParams) => [...discoveryKeys.all(e), 'plan', p] as const,
  people: (e: string) => [...discoveryKeys.all(e), 'people'] as const,
  person: (e: string, id: string) => [...discoveryKeys.all(e), 'person', id] as const,
  check: (e: string, name: string, email: string) =>
    [...discoveryKeys.all(e), 'person-check', name, email] as const,
  questionnaire: (e: string, id: string) =>
    [...discoveryKeys.all(e), 'interview-questionnaire', id] as const,
  preview: (e: string, qid: string, department: string) =>
    [...discoveryKeys.all(e), 'preview', qid, department] as const,
  round: (e: string, id: string) => [...discoveryKeys.all(e), 'round', id] as const,
};

/** While a transcript is read, its progress moves in the list and the interview (T-0187). */
const READING_POLL_MS = 5000;

export function useInterviewsQuery(e: string) {
  return useQuery({
    queryKey: sessionKeys.interviews(e),
    queryFn: () => listInterviews(e),
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.groups.some((g) => g.items.some((i) => i.state.kind === 'reading'))
        ? READING_POLL_MS
        : false,
  });
}

export function useInterviewQuery(e: string, id: string | null) {
  return useQuery({
    queryKey: sessionKeys.interview(e, id ?? ''),
    queryFn: () => getInterview(e, id!),
    enabled: !!id,
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.interview.state.kind === 'reading' ? READING_POLL_MS : false,
  });
}

/** The plan for the add form; recomputed as the title, type and areas change. Nothing is written. */
export function useInterviewPlanQuery(e: string, p: PlanParams, enabled: boolean) {
  return useQuery({
    queryKey: sessionKeys.plan(e, p),
    queryFn: () => getInterviewPlan(e, p),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function usePeopleQuery(e: string) {
  return useQuery({ queryKey: sessionKeys.people(e), queryFn: () => listPeople(e), retry: false });
}

export function usePersonQuery(e: string, id: string | null) {
  return useQuery({
    queryKey: sessionKeys.person(e, id ?? ''),
    queryFn: () => getPerson(e, id!),
    enabled: !!id,
    retry: false,
  });
}

/** The same-person check while a name is typed (rule 6). Read-only. */
export function usePersonCheckQuery(e: string, name: string, email: string, enabled: boolean) {
  return useQuery({
    queryKey: sessionKeys.check(e, name, email),
    queryFn: () => checkPerson(e, { full_name: name, ...(email ? { email } : {}) }),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useInterviewQuestionnaireQuery(e: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: sessionKeys.questionnaire(e, id),
    queryFn: () => getInterviewQuestionnaire(e, id),
    enabled,
    retry: false,
  });
}

export function useQuestionnairePreviewQuery(
  e: string,
  questionnaireId: string,
  department: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: sessionKeys.preview(e, questionnaireId, department),
    queryFn: () => getQuestionnairePreview(e, questionnaireId, department || undefined),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useRoundQuery(e: string, id: string | null) {
  return useQuery({
    queryKey: sessionKeys.round(e, id ?? ''),
    queryFn: () => getRound(e, id!),
    enabled: !!id,
    retry: false,
  });
}

/** A discovery write: on success everything discovery shows for the engagement is read again. */
export function useDiscoveryWrite<V, R>(e: string, fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKeys.all(e) }),
  });
}

/** An answer as the server words it (`formatAnswer` in the questionnaires service). */
export function answerText(value: AnswerValue): string {
  if ('text' in value) return value.text;
  if ('rating' in value) return `${value.rating} of 5`;
  if ('option' in value) return value.option;
  return value.options.join(', ');
}

/**
 * The questionnaire with one live answer saved, as the server will read it
 * back: the question carries the answer and its evidence, and `answered`
 * counts the questions with an answer, as the server counts them.
 */
export function withLiveAnswer(
  q: InterviewQuestionnaire,
  runQuestionId: string,
  value: AnswerValue,
  saved: LiveAnswerResult,
  answeredAt: string,
): InterviewQuestionnaire {
  const questions = q.questions.map((x) =>
    x.run_question_id === runQuestionId
      ? {
          ...x,
          answer: {
            answer_id: saved.answer_id,
            text: answerText(value),
            channel: 'live',
            answered_at: answeredAt,
            evidence_number: saved.evidence.number,
            held: saved.evidence.held,
          },
        }
      : x,
  );
  return { ...q, questions, answered: questions.filter((x) => x.answer).length };
}

/**
 * A live answer typed in during the interview (E12-F08). The saved answer
 * and the new answered count go into the questionnaire and the interview
 * straight from the write's result, so Save is answered at once; the rest
 * of discovery (coverage, the list) is read again in the background, not
 * waited on. `useDiscoveryWrite` waits for that read-back, which kept the
 * button busy and the count still for seconds.
 */
export function useRecordLiveAnswer(e: string, interviewId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { run_question_id: string; value: AnswerValue }) =>
      recordLiveAnswer(e, interviewId, vars),
    onSuccess: (saved, vars) => {
      const before = qc.getQueryData<InterviewQuestionnaire>(
        sessionKeys.questionnaire(e, interviewId),
      );
      if (before) {
        const after = withLiveAnswer(
          before,
          vars.run_question_id,
          vars.value,
          saved,
          new Date().toISOString(),
        );
        qc.setQueryData(sessionKeys.questionnaire(e, interviewId), after);
        qc.setQueryData<InterviewDetail>(sessionKeys.interview(e, interviewId), (d) =>
          d?.questionnaire
            ? { ...d, questionnaire: { ...d.questionnaire, answered: after.answered } }
            : d,
        );
      }
      void qc.invalidateQueries({ queryKey: discoveryKeys.all(e) });
    },
  });
}
